import type { LucideIcon } from 'lucide-react';
import { cn } from '../lib/utils';
import { Card } from './ui/card';
import { Skeleton } from './ui/skeleton';

interface Props {
  label: string;
  value: number | string | null;
  hint?: string;
  icon?: LucideIcon;
  accent?: 'brand' | 'open' | 'accepted' | 'completed' | 'cancelled' | 'error';
  format?: (n: number) => string;
}

const accentMap = {
  brand: 'bg-brand-50 text-brand-700 dark:bg-brand-900/40 dark:text-brand-500',
  open: 'bg-status-open-tint text-status-open-on-tint',
  accepted: 'bg-status-accepted-tint text-status-accepted-on-tint',
  completed: 'bg-status-completed-tint text-status-completed-on-tint',
  cancelled: 'bg-status-cancelled-tint text-status-cancelled-on-tint',
  error: 'bg-error-tint text-error-on-tint',
} as const;

export function StatCard({ label, value, hint, icon: Icon, accent = 'brand', format }: Props) {
  const display =
    value == null
      ? null
      : typeof value === 'number'
        ? (format ? format(value) : value.toLocaleString())
        : value;

  return (
    <Card className="p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium uppercase tracking-wider text-text-muted">{label}</p>
          {display == null ? (
            <Skeleton className="mt-3 h-8 w-24" />
          ) : (
            <p className="tnum mt-2 text-3xl font-bold tracking-tight text-text">{display}</p>
          )}
          {hint && <p className="mt-1 text-xs text-text-muted">{hint}</p>}
        </div>
        {Icon && (
          <div className={cn('flex h-9 w-9 items-center justify-center rounded-lg', accentMap[accent])}>
            <Icon className="h-4 w-4" />
          </div>
        )}
      </div>
    </Card>
  );
}
